import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStocksGraph, getRecentlyViewed, addRecentlyViewed, searchStockName, getMarketIndices } from '../../../api/api';
import { getPricePercentChange } from '../../../api/stocks_api';
import { Box, Typography } from '@mui/material';
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

const CustomChart = ({ data, color, onHover, period }) => {
  const canvasRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const sym = getCurrencySymbol();

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);

    if (period === "1D") {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    if (period === "5D") {
      return date.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }

    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.length) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const padding = 25;

    ctx.clearRect(0, 0, width, height);

    const values = data.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const points = data.map((d, i) => ({
      x: padding + (i / (data.length - 1)) * (width - padding * 2),
      y: height - padding - ((d.value - min) / range) * (height - padding * 2),
    }));

    // Subtle horizontal grid - use CSS variable
    const computedStyle = getComputedStyle(document.documentElement);
    const borderColor = computedStyle.getPropertyValue('--color-border-primary').trim();
    ctx.strokeStyle = borderColor || "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;

    for (let i = 1; i <= 3; i++) {
      const y = padding + ((height - padding * 2) / 3) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Area fill with theme-aware colors
    const gradient = ctx.createLinearGradient(0, padding, 0, height);
    if (color.includes('success')) {
      gradient.addColorStop(0, "rgba(16,185,129,0.3)");
    } else {
      gradient.addColorStop(0, "rgba(239,68,68,0.3)");
    }
    gradient.addColorStop(1, "rgba(0,0,0,0)");

    ctx.beginPath();
    ctx.moveTo(points[0].x, height - padding);
    ctx.lineTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }

    ctx.lineTo(points[points.length - 1].x, height - padding);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Glow line - resolve color from CSS variable
    const lineColor = color.startsWith('var(--')
      ? computedStyle.getPropertyValue(color.substring(6, color.length - 1)).trim()
      : color;
    ctx.shadowColor = lineColor;
    ctx.shadowBlur = 8;

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }

    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.shadowBlur = 0;

    // X Axis Labels - use text color from CSS
    const textColor = computedStyle.getPropertyValue('--color-text-muted').trim();
    ctx.fillStyle = textColor || "rgba(255,255,255,0.4)";
    ctx.font = "10px sans-serif";

    const labelCount = 3;
    for (let i = 0; i <= labelCount; i++) {
      const index = Math.floor((i / labelCount) * (data.length - 1));
      const point = points[index];
      const label = formatTime(data[index].timestamp);

      ctx.fillText(label, point.x - 20, height - 5);
    }

    // Hovered point
    if (hoveredIndex !== null && points[hoveredIndex]) {
      const p = points[hoveredIndex];

      ctx.beginPath();
      ctx.moveTo(p.x, padding);
      ctx.lineTo(p.x, height - padding);
      ctx.strokeStyle = borderColor || "rgba(255,255,255,0.15)";
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = lineColor;
      ctx.fill();
      ctx.strokeStyle = computedStyle.getPropertyValue('--color-text-primary').trim() || "#fff";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    canvas.pointsData = points;
  }, [data, color, hoveredIndex, period]);

  const handleMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas?.pointsData) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    let index = Math.round((x / rect.width) * (data.length - 1));
    index = Math.max(0, Math.min(data.length - 1, index));

    setHoveredIndex(index);

    const candle = data[index];

    setTooltip({
      x: canvas.pointsData[index].x,
      y: canvas.pointsData[index].y,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      time: formatTime(candle.timestamp),
    });

    onHover?.(candle);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseMove={handleMove}
        onMouseLeave={() => {
          setHoveredIndex(null);
          setTooltip(null);
        }}
        style={{
          width: "100%",
          height: "100%",
          cursor: hoveredIndex !== null ? "crosshair" : "default",
        }}
      />

      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y - 95,
            transform: "translateX(-50%)",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "12px",
            padding: "10px 14px",
            fontSize: "12px",
            color: "var(--color-text-primary)",
            pointerEvents: "none",
            boxShadow: "var(--shadow-xl)",
            minWidth: "140px",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography variant="subtitle" color="text.secondary">
            {tooltip.time}
          </Typography>
          <Typography variant="amountSm">
            {sym}{tooltip.open != null ? fmtL(tooltip.open, 2) : "--"}
          </Typography>
          <Typography variant="amountSm">
            {sym}{tooltip.high != null ? fmtL(tooltip.high, 2) : "--"}
          </Typography>
          <Typography variant="amountSm">
            {sym}{tooltip.low != null ? fmtL(tooltip.low, 2) : "--"}
          </Typography>
          <Typography variant="amountSm">
            {sym}{tooltip.close != null ? fmtL(tooltip.close, 2) : "--"}
          </Typography>



          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
              <span style={{ color: "var(--color-text-muted)", fontWeight: 500 }}>O:</span>
              <span style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                {sym}{tooltip.open != null ? fmtL(tooltip.open, 2) : "--"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
              <span style={{ color: "var(--color-text-muted)", fontWeight: 500 }}>H:</span>
              <span style={{ fontWeight: 600, color: "var(--color-success)" }}>
                {sym}{tooltip.high != null ? fmtL(tooltip.high, 2) : "--"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
              <span style={{ color: "var(--color-text-muted)", fontWeight: 500 }}>L:</span>
              <span style={{ fontWeight: 600, color: "var(--color-error)" }}>
                {sym}{tooltip.low != null ? fmtL(tooltip.low, 2) : "--"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                marginTop: "4px",
                paddingTop: "6px",
                borderTop: "1px solid var(--color-border-primary)",
              }}
            >
              <span style={{ color: "var(--color-text-muted)", fontWeight: 500 }}>C:</span>
              <span style={{ fontWeight: 700, color: "var(--color-text-primary)", fontSize: "13px" }}>
                {sym}{tooltip.close != null ? fmtL(tooltip.close, 2) : "--"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const IndexCard = ({ name, symbol, value, change, percent, color }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('5D');
  const [chartData, setChartData] = useState([]);
  const [quoteData, setQuoteData] = useState(null);
  const [hoveredData, setHoveredData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const periods = ['1D', '5D', '1M', '6M', 'YTD', '1Y'];

  useEffect(() => {
    const fetchChart = async () => {
      try {
        setLoading(true);

        const res = await getStocksGraph(symbol, selectedPeriod);
        if (!res?.success || !res?.data?.prices) {
          setChartData([]);
          return;
        }

        const formatted = res.data.prices.map((item) => ({
          timestamp: item.timestamp,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          value: item.close,
        }));

        setChartData(formatted);
      } catch (err) {
        console.error("Mini chart error:", err);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChart();
  }, [symbol, selectedPeriod]);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await getPricePercentChange(symbol);

        if (res?.success && res?.data) {
          setQuoteData(res.data);
        } else {
          setQuoteData(null);
        }
      } catch (err) {
        console.error("Quote fetch error:", err);
        setQuoteData(null);
      }
    };

    fetchQuote();
  }, [symbol]);

  const handleHover = (data) => {
    setHoveredData(data);
  };

  const cardColor = quoteData?.change >= 0 ? 'var(--color-success)' : 'var(--color-error)';
  const sym = getCurrencySymbol();

  return (
    <div
      ref={cardRef}
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '24px',
        padding: '24px',
        minWidth: '300px',
        position: 'relative',
        border: '1px solid var(--color-border-primary)',
        boxShadow: 'var(--shadow-md)',
        transition: 'all 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography
            variant="title"
            color="text.primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/stocks/${symbol}/details/summary`)}
          >
            {name}
          </Typography>

          <Typography variant="subtitle" color="text.secondary">
            {symbol}
          </Typography>

        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: '180px' }}>
          {periods.map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              style={{
                fontSize: 10,
                padding: '5px 9px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: selectedPeriod === period ? 'var(--color-primary)' : 'var(--color-surface-hover)',
                color: selectedPeriod === period ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                fontWeight: selectedPeriod === period ? 600 : 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (selectedPeriod !== period) {
                  e.target.style.backgroundColor = 'var(--color-surface-active)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedPeriod !== period) {
                  e.target.style.backgroundColor = 'var(--color-surface-hover)';
                }
              }}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Value */}
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <Typography variant="amountLg" color="text.primary">
          {sym}{quoteData?.price ? fmtL(quoteData.price, 2) : "--"}
        </Typography>

        <div
          style={{
            color: cardColor,
            fontWeight: 600,
            fontSize: '14px',
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Typography
            variant="amountSm"
            sx={{ color: cardColor, display: 'flex', gap: 1 }}
          >
            {quoteData?.change
              ? `${quoteData.change >= 0 ? "+" : ""}${fmtL(quoteData.change, 2)}`
              : "--"}
          </Typography>

          <span style={{ color: 'var(--color-text-muted)' }}>•</span>
          <span>
            {quoteData?.change_pct ? `${quoteData.change_pct >= 0 ? '+' : ''}${fmtL(quoteData.change_pct, 2)}%` : "--"}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 140, marginTop: 16 }}>
        {loading ? (
          <div
            style={{
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: '14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid var(--color-border-primary)',
                  borderTopColor: 'var(--color-text-muted)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              Loading chart...
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <CustomChart data={chartData} color={cardColor} period={selectedPeriod} onHover={handleHover} />
        ) : (
          <div
            style={{
              color: 'var(--color-error)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: '14px',
            }}
          >
            No chart data available
          </div>
        )}
      </div>

    </div>
  );
};

const TickerSelector = ({ onTickersSelected, currentTickers }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTickers, setSelectedTickers] = useState(currentTickers);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Update selected tickers when currentTickers prop changes
  useEffect(() => {
    setSelectedTickers(currentTickers);
  }, [currentTickers]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchStockName(searchQuery);

        if (response.success && response.data) {
          // Map API response to our format and remove duplicates
          const mappedResults = response.data.map((item) => ({
            symbol: item.ticker,
            name: item.company_name || item.ticker,
          }));

          const uniqueResults = mappedResults.filter(
            (ticker, index, self) =>
              index === self.findIndex((t) => t.symbol === ticker.symbol)
          );
          setSearchResults(uniqueResults);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleToggleTicker = (symbol, name) => {
    setSelectedTickers((prev) => {
      if (prev.some((t) => t.symbol === symbol)) {
        // Remove ticker - but ensure we always have at least 1
        if (prev.length > 1) {
          return prev.filter((t) => t.symbol !== symbol);
        }
        return prev;
      } else {
        // Add ticker - but limit to 3
        if (prev.length < 3) {
          return [...prev, { symbol, name }];
        }
        return prev;
      }
    });
  };

  const handleApply = () => {
    // Ensure exactly 3 tickers
    if (selectedTickers.length === 3) {
      onTickersSelected(selectedTickers);
      setIsOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const isTickerSelected = (symbol) => {
    return selectedTickers.some((t) => t.symbol === symbol);
  };

  return (
    <div style={{ position: 'relative' }}>
      {isOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--color-bg-overlay)',
              zIndex: 999,
            }}
            onClick={() => {
              setIsOpen(false);
              setSearchQuery('');
              setSearchResults([]);
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: '16px',
              padding: '16px',
              width: '340px',
              maxHeight: '500px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search tickers (e.g., AAPL, Apple)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: '10px',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  outline: 'revert',
                }}
                autoFocus
              />
            </div>

            {/* Selected Tickers Section */}
            {selectedTickers.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Selected ({selectedTickers.length}/3)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedTickers.map((ticker) => (
                    <div
                      key={ticker.symbol}
                      style={{
                        padding: '10px 12px',
                        backgroundColor: 'var(--color-primary-lighter)',
                        border: '1px solid var(--color-primary)',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontSize: '13px' }}>
                          {ticker.symbol}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: '11px', marginTop: '1px' }}>
                          {ticker.name}
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleTicker(ticker.symbol, ticker.name)}
                        disabled={selectedTickers.length === 1}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: selectedTickers.length === 1 ? 'var(--color-text-muted)' : 'var(--color-error)',
                          cursor: selectedTickers.length === 1 ? 'not-allowed' : 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: selectedTickers.length === 1 ? 0.5 : 1,
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="12" cy="12" r="10" strokeWidth="2" />
                          <path d="M15 9l-6 6M9 9l6 6" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            <div style={{ marginBottom: '12px', color: 'var(--color-text-muted)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {searchQuery.trim() ? 'Search Results' : 'Start typing to search'}
            </div>

            {isSearching ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 20px',
                  color: 'var(--color-text-muted)',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid var(--color-border-primary)',
                      borderTopColor: 'var(--color-text-muted)',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                  Searching...
                </div>
              </div>
            ) : searchQuery.trim() && searchResults.length === 0 ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--color-text-muted)',
                  fontSize: '13px',
                }}
              >
                No tickers found for "{searchQuery}"
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '240px', overflowY: 'auto' }}>
                {searchResults.map((ticker) => {
                  const selected = isTickerSelected(ticker.symbol);
                  const canSelect = !selected && selectedTickers.length < 3;
                  const cannotSelectReason = selected ? 'Already selected' : selectedTickers.length >= 3 ? 'Maximum 3 tickers' : null;

                  return (
                    <div
                      key={ticker.symbol}
                      onClick={() => {
                        if (canSelect || selected) {
                          handleToggleTicker(ticker.symbol, ticker.name);
                        }
                      }}
                      style={{
                        padding: '12px',
                        backgroundColor: selected
                          ? 'var(--color-primary-lighter)'
                          : 'var(--color-surface-hover)',
                        border: `1px solid ${selected
                          ? 'var(--color-primary)'
                          : 'var(--color-border-primary)'
                          }`,
                        borderRadius: '10px',
                        cursor: (canSelect || selected) ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        opacity: cannotSelectReason && !selected ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (canSelect && !selected) {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface-active)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontSize: '14px' }}>
                            {ticker.symbol}
                          </div>
                          <div style={{ color: 'var(--color-text-muted)', fontSize: '12px', marginTop: '2px' }}>
                            {ticker.name}
                          </div>
                          {cannotSelectReason && !selected && (
                            <div style={{ color: 'var(--color-error)', fontSize: '10px', marginTop: '4px' }}>
                              {cannotSelectReason}
                            </div>
                          )}
                        </div>
                        {selected && (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="var(--color-primary)" />
                            <path
                              d="M7 12l3 3 7-7"
                              stroke="var(--color-text-inverse)"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                onClick={handleApply}
                disabled={selectedTickers.length !== 3}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: selectedTickers.length === 3 ? 'var(--color-primary)' : 'var(--color-surface-hover)',
                  color: selectedTickers.length === 3 ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: selectedTickers.length === 3 ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (selectedTickers.length === 3) {
                    e.target.style.backgroundColor = 'var(--color-primary-dark)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTickers.length === 3) {
                    e.target.style.backgroundColor = 'var(--color-primary)';
                  }
                }}
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                style={{
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-surface-hover)',
                  color: 'var(--color-text-muted)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--color-surface-active)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--color-surface-hover)';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          /* Custom scrollbar styling */
          div::-webkit-scrollbar {
            width: 6px;
          }
          
          div::-webkit-scrollbar-track {
            background: var(--color-bg-secondary);
            border-radius: 10px;
          }
          
          div::-webkit-scrollbar-thumb {
            background: var(--color-text-muted);
            border-radius: 10px;
            opacity: 0.3;
          }
          
          div::-webkit-scrollbar-thumb:hover {
            opacity: 0.5;
          }
          
          /* Firefox scrollbar */
          div {
            scrollbar-width: thin;
            scrollbar-color: var(--color-text-muted) var(--color-bg-secondary);
          }
        `}
      </style>
    </div>
  );
};

const MarketIndexCard = ({ symbol, name, currentValue, changeValue, changePct, marketOpen, source }) => {
  const isPositive = changeValue >= 0;
  const color = isPositive ? 'var(--color-success)' : 'var(--color-error)';

  return (
    <div
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid var(--color-border-primary)',
        boxShadow: 'var(--shadow-md)',
        transition: 'all 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography variant="title">
            {name}{"     "}
          </Typography>
          <Typography variant="subtitle" color="text.secondary">
            {symbol}
          </Typography>
        </div>
        <div
          style={{
            fontSize: '10px',
            fontWeight: 600,
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: marketOpen ? 'var(--color-success-light)' : 'var(--color-surface-hover)',
            color: marketOpen ? 'var(--color-success)' : 'var(--color-text-muted)',
          }}
        >
          {marketOpen ? 'LIVE' : 'CLOSED'}
        </div>
      </div>

      <Box mt={2}>
        {/* Current Value */}
        <Typography
          variant="amountLg"
          sx={{
            color: "var(--color-text-primary)",
            fontWeight: "var(--font-weight-bold)",
            letterSpacing: "-0.02em",
          }}
        >
          {currentValue !== null && currentValue !== undefined
            ? currentValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            : "--"}
        </Typography>

        {/* Change Section */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color,
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          {/* Triangle Indicator */}
          <Box
            component="span"
            sx={{
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            {isPositive ? "▲" : "▼"}
          </Box>

          {/* Absolute Change */}
          <Typography
            variant="amountSm"
            sx={{
              color,
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            {isPositive ? "+" : ""}
            {changeValue !== null && changeValue !== undefined
              ? fmtL(changeValue, 2)
              : "--"}
          </Typography>

          {/* Percentage Change */}
          <Typography
            variant="amountSm"
            sx={{
              color,
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            (
            {isPositive ? "+" : ""}
            {changePct !== null && changePct !== undefined
              ? fmtL(changePct, 2)
              : "--"}
            %)
          </Typography>
        </Box>
      </Box>

    </div>
  );
};

const IndicesChart = () => {
  const [displayedTickers, setDisplayedTickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketIndices, setMarketIndices] = useState([]);
  const [loadingIndices, setLoadingIndices] = useState(true);
  const DEFAULT_TICKERS = [
    { symbol: 'AAPL', name: 'Apple Inc.' },
    { symbol: 'MSFT', name: 'Microsoft Corporation' },
    { symbol: 'NVDA', name: 'NVIDIA Corporation' },
  ];

  useEffect(() => {
    const fetchRecentlyViewed = async () => {
      try {
        setLoading(true);
        const response = await getRecentlyViewed();

        if (response.success && response.data?.length > 0) {
          // Get unique recent tickers - map from API format
          const recentTickers = response.data
            .map((item) => ({
              symbol: item.ticker || item.symbol,
              name: item.company_name || item.name || item.ticker || item.symbol,
            }))
            .filter((item) => item.symbol)
            // Remove duplicates based on symbol
            .filter(
              (ticker, index, self) =>
                index === self.findIndex((t) => t.symbol === ticker.symbol)
            );

          let tickersToShow = [];

          if (recentTickers.length >= 3) {
            // Show first 3 unique tickers from recently viewed
            tickersToShow = recentTickers.slice(0, 3);
          } else if (recentTickers.length === 2) {
            // Add one default that's not already in the list
            const missingDefault = DEFAULT_TICKERS.find(
              (dt) => !recentTickers.some((rt) => rt.symbol === dt.symbol)
            );
            tickersToShow = [...recentTickers, missingDefault];
          } else if (recentTickers.length === 1) {
            // Add two defaults that are not in the list
            const missingDefaults = DEFAULT_TICKERS.filter(
              (dt) => !recentTickers.some((rt) => rt.symbol === dt.symbol)
            );
            tickersToShow = [...recentTickers, ...missingDefaults.slice(0, 2)];
          } else {
            // Empty - show all defaults
            tickersToShow = DEFAULT_TICKERS;
          }

          // Final check: ensure exactly 3 unique tickers
          const uniqueTickersToShow = tickersToShow
            .filter(
              (ticker, index, self) =>
                index === self.findIndex((t) => t.symbol === ticker.symbol)
            )
            .slice(0, 3);

          setDisplayedTickers(uniqueTickersToShow);
        } else {
          // No recently viewed - show defaults
          setDisplayedTickers(DEFAULT_TICKERS);
        }
      } catch (error) {
        console.error('Error fetching recently viewed:', error);
        setDisplayedTickers(DEFAULT_TICKERS);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentlyViewed();
  }, []);

  useEffect(() => {
    const fetchMarketIndices = async () => {
      try {
        setLoadingIndices(true);
        const response = await getMarketIndices();

        if (response.success && response.data?.indices) {
          setMarketIndices(response.data.indices);
        } else {
          setMarketIndices([]);
        }
      } catch (error) {
        console.error('Error fetching market indices:', error);
        setMarketIndices([]);
      } finally {
        setLoadingIndices(false);
      }
    };

    fetchMarketIndices();

    const interval = setInterval(fetchMarketIndices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleTickersSelected = async (newTickers) => {
    // Ensure no duplicates in the selected tickers
    const uniqueTickers = newTickers.filter(
      (ticker, index, self) =>
        index === self.findIndex((t) => t.symbol === ticker.symbol)
    );

    // Ensure exactly 3 tickers
    if (uniqueTickers.length !== 3) {
      console.error('Must select exactly 3 unique tickers');
      return;
    }

    // Add selected tickers to recently viewed
    for (const ticker of uniqueTickers) {
      try {
        await addRecentlyViewed(ticker.symbol);
      } catch (error) {
        console.error(`Error adding ${ticker.symbol} to recently viewed:`, error);
      }
    }

    setDisplayedTickers(uniqueTickers);
  };

  return (
    <Box
      sx={{
        px: { xs: "var(--spacing-md)", sm: "var(--spacing-lg)", md: "var(--spacing-xl)" },
        pt: { xs: "var(--spacing-md)", sm: "var(--spacing-lg)", md: "var(--spacing-xl)" }
      }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        {/* <Typography variant="title">
          Market Overview
        </Typography> */}

        <TickerSelector
          onTickersSelected={handleTickersSelected}
          currentTickers={displayedTickers}
        />
      </div>

      {(loading || loadingIndices) ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            color: 'var(--color-text-muted)',
            fontSize: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '3px solid var(--color-border-primary)',
                borderTopColor: 'var(--color-text-muted)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <Typography variant="paragraph" color="text.secondary">
              Loading market data...
            </Typography>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {marketIndices.length > 0 && (
            <div>
              <Box
                sx={{
                  display: "grid",
                  gap: "20px",
                  width: "100%",
                  gridTemplateColumns: {
                    xs: "1fr",              // 📱 Mobile = 1
                    sm: "repeat(2, 1fr)",   // Tablet = 2
                    md: "repeat(3, 1fr)",   // Desktop = 3
                  },
                }}
              >
                {marketIndices.map((index) => (
                  <MarketIndexCard
                    key={index.symbol}
                    symbol={index.symbol}
                    name={index.name}
                    currentValue={index.current_value}
                    changeValue={index.change_value}
                    changePct={index.change_pct}
                    marketOpen={index.market_open}
                    source={index.source}
                  />
                ))}
              </Box>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default IndicesChart;